import { useState } from 'react';
import { getSurveySettings, setSurveySettings, renderSurveyMessage } from './leadActivity';

const inp = { width: '100%', padding: '8px 12px', border: '1.5px solid #e0e0e0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none', marginBottom: 10 };

// ── Module 17: Agent Leave Toggle ──────────────────────────────
function AgentLeaveToggle({ agents }) {
  const [leaves, setLeaves] = useState({});
  const [toast, setToast] = useState('');
  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2000); };

  const toggle = (id) => {
    setLeaves(prev => ({ ...prev, [id]: !prev[id] }));
    flash(`✅ Agent status updated (preview mode — backend integration pending)`);
  };

  const agentList = agents || [];

  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #eef0f2', padding: '18px 22px', marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: '#1a1a2e', marginBottom: 4 }}>👤 Agent Leave Toggle</div>
      <div style={{ fontSize: 11, color: '#aaa', marginBottom: 14 }}>Turn off new lead assignments for agents on leave</div>
      {agentList.map(a => (
        <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f8f8f8' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e' }}>{a.name || a.agent_name}</div>
            <div style={{ fontSize: 11, color: leaves[a.id] ? '#f44336' : '#25d366', marginTop: 2 }}>
              {leaves[a.id] ? '🔴 On Leave — no new leads' : '🟢 Active — receiving leads'}
            </div>
          </div>
          <div onClick={() => toggle(a.id)} style={{
            width: 44, height: 24, borderRadius: 12, cursor: 'pointer', transition: 'background 0.2s', position: 'relative',
            background: leaves[a.id] ? '#f44336' : '#25d366',
          }}>
            <div style={{
              position: 'absolute', top: 2, left: leaves[a.id] ? 22 : 2, width: 20, height: 20,
              borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }} />
          </div>
        </div>
      ))}
      {toast && <div style={{ marginTop: 10, fontSize: 12, color: '#25d366', fontWeight: 600 }}>{toast}</div>}
    </div>
  );
}

// ── Module 16: Auto-assign Rules ───────────────────────────────
function AutoAssignRules({ agents }) {
  const [rules, setRules] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ condition: 'product', value: '', agent_id: '' });
  const [toast, setToast] = useState('');
  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2000); };

  const agentList = agents || [];

  const agentName = (id) => agentList.find(a => String(a.id) === String(id))?.name || 'Agent ' + id;

  const addRule = () => {
    if (!form.value || !form.agent_id) { flash('❌ Fill all fields'); return; }
    setRules([...rules, { ...form, id: Date.now(), active: true }]);
    setForm({ condition: 'product', value: '', agent_id: '' });
    setShowAdd(false);
    flash('✅ Rule added (preview mode)');
  };

  const toggleRule = (id) => setRules(rules.map(r => r.id === id ? { ...r, active: !r.active } : r));
  const removeRule = (id) => { setRules(rules.filter(r => r.id !== id)); flash('🗑 Rule removed'); };

  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #eef0f2', padding: '18px 22px', marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#1a1a2e' }}>⚡ Auto-assign Rules</div>
        <button onClick={() => setShowAdd(true)} style={{ padding: '6px 14px', background: '#25d366', color: '#fff', border: 'none', borderRadius: 20, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 2px 6px rgba(37,211,102,0.3)' }}>+ Add Rule</button>
      </div>
      <div style={{ fontSize: 11, color: '#aaa', marginBottom: 14 }}>Assign leads automatically by product, source or keyword</div>

      {rules.map(r => (
        <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #f8f8f8', opacity: r.active ? 1 : 0.5 }}>
          <div onClick={() => toggleRule(r.id)} style={{
            width: 36, height: 20, borderRadius: 10, cursor: 'pointer', position: 'relative', flexShrink: 0,
            background: r.active ? '#25d366' : '#ccc', transition: 'background 0.2s',
          }}>
            <div style={{ position: 'absolute', top: 2, left: r.active ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
          </div>
          <div style={{ flex: 1, fontSize: 12, color: '#444' }}>
            If <strong>{r.condition}</strong> = <strong>{r.value}</strong> → assign to <strong>{agentName(r.agent_id)}</strong>
          </div>
          <button onClick={() => removeRule(r.id)} style={{ background: 'none', border: 'none', color: '#f44336', cursor: 'pointer', fontSize: 14 }}>✕</button>
        </div>
      ))}

      {showAdd && (
        <div style={{ marginTop: 14, padding: 14, background: '#f9f9f9', borderRadius: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
            <select style={{ ...inp, marginBottom: 0 }} value={form.condition} onChange={e => setForm({ ...form, condition: e.target.value })}>
              <option value="product">Product</option>
              <option value="source">Source</option>
              <option value="keyword">Keyword</option>
            </select>
            <input style={{ ...inp, marginBottom: 0 }} placeholder="Value" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} />
            <select style={{ ...inp, marginBottom: 0 }} value={form.agent_id} onChange={e => setForm({ ...form, agent_id: e.target.value })}>
              <option value="">Select agent</option>
              {agentList.map(a => <option key={a.id} value={a.id}>{a.name || a.agent_name}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowAdd(false)} style={{ flex: 1, padding: '8px 0', background: '#f0f0f0', color: '#333', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
            <button onClick={addRule} style={{ flex: 2, padding: '8px 0', background: '#25d366', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Add Rule</button>
          </div>
        </div>
      )}
      {toast && <div style={{ marginTop: 10, fontSize: 12, color: '#25d366', fontWeight: 600 }}>{toast}</div>}
    </div>
  );
}

// ── Module 23: RNR Automation ──────────────────────────────────
function RNRAutomation() {
  const [enabled, setEnabled] = useState(false);
  const [stages, setStages] = useState([
    { id: 1, day: 1,  channel: 'whatsapp', message: 'Hi {name}, following up on your enquiry. Are you still interested?' },
    { id: 2, day: 3,  channel: 'whatsapp', message: 'Hi {name}, we noticed you haven\'t responded. Can we help you?' },
    { id: 3, day: 7,  channel: 'email',    message: 'Dear {name}, we\'d love to hear from you. Please contact us.' },
    { id: 4, day: 14, channel: 'whatsapp', message: 'Hi {name}, last follow-up. Let us know if you\'re interested.' },
  ]);

  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #eef0f2', padding: '18px 22px', marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#1a1a2e' }}>🔄 RNR Automation</div>
        <div onClick={() => setEnabled(!enabled)} style={{
          width: 44, height: 24, borderRadius: 12, cursor: 'pointer', position: 'relative',
          background: enabled ? '#25d366' : '#ccc', transition: 'background 0.2s',
        }}>
          <div style={{ position: 'absolute', top: 2, left: enabled ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
        </div>
      </div>
      <div style={{ fontSize: 11, color: '#aaa', marginBottom: 14 }}>Auto follow-up for leads who don't respond (requires backend scheduler)</div>

      {stages.map((s, i) => (
        <div key={s.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 0', borderBottom: '1px solid #f8f8f8', opacity: enabled ? 1 : 0.5 }}>
          <div style={{ background: '#075e54', color: '#fff', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0, marginTop: 2 }}>{i+1}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#333', marginBottom: 4 }}>Day {s.day} · {s.channel === 'whatsapp' ? '💬 WhatsApp' : '📧 Email'}</div>
            <div style={{ fontSize: 11, color: '#888', lineHeight: 1.5 }}>{s.message}</div>
          </div>
        </div>
      ))}
      {!enabled && (
        <div style={{ marginTop: 10, padding: '8px 12px', background: '#fff8e1', borderRadius: 8, fontSize: 11, color: '#e65100' }}>
          ⚠️ Enable the toggle to activate RNR automation (backend scheduler required for actual sending)
        </div>
      )}
    </div>
  );
}

// ── Module 27: Scheduled Reports ──────────────────────────────
function ScheduledReports() {
  const [reports, setReports] = useState([
    { id: 1, name: 'Daily Lead Summary', frequency: 'daily', channel: 'whatsapp', time: '09:00', active: true },
    { id: 2, name: 'Weekly Performance', frequency: 'weekly', channel: 'email', time: '08:00', active: false },
  ]);
  const [toast, setToast] = useState('');
  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2000); };

  const toggle = (id) => {
    setReports(reports.map(r => r.id === id ? { ...r, active: !r.active } : r));
    flash('✅ Report schedule updated (preview mode)');
  };

  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #eef0f2', padding: '18px 22px', marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: '#1a1a2e', marginBottom: 4 }}>📊 Scheduled Reports</div>
      <div style={{ fontSize: 11, color: '#aaa', marginBottom: 14 }}>Auto-send reports via WhatsApp or email (requires backend scheduler)</div>
      {reports.map(r => (
        <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0', borderBottom: '1px solid #f8f8f8' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e' }}>{r.name}</div>
            <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>
              {r.frequency === 'daily' ? '📅 Daily' : '📅 Weekly'} · {r.channel === 'whatsapp' ? '💬 WhatsApp' : '📧 Email'} · {r.time}
            </div>
          </div>
          <div onClick={() => toggle(r.id)} style={{
            width: 44, height: 24, borderRadius: 12, cursor: 'pointer', position: 'relative',
            background: r.active ? '#25d366' : '#ccc', transition: 'background 0.2s',
          }}>
            <div style={{ position: 'absolute', top: 2, left: r.active ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
          </div>
        </div>
      ))}
      {toast && <div style={{ marginTop: 10, fontSize: 12, color: '#25d366', fontWeight: 600 }}>{toast}</div>}
    </div>
  );
}

// ── Module 29: Feedback survey settings ───────────────────────
function SurveySettings() {
  const init = getSurveySettings();
  const [autoSend, setAutoSend] = useState(init.autoSend);
  const [delay, setDelay] = useState(init.delay);
  const [message, setMessage] = useState(init.message);
  const [toast, setToast] = useState('');
  const save = () => { setSurveySettings({ autoSend, delay, message }); setToast('✅ Survey settings saved'); setTimeout(() => setToast(''), 2000); };
  const preview = renderSurveyMessage(message, { name: 'Ravi', product: 'Demo product', link: 'https://vbot.co.in/feedback/123456' });
  return (
    <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 16, maxWidth: 560 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: '#1a1a2e', marginBottom: 4 }}>📋 Feedback Survey</div>
      <div style={{ fontSize: 12, color: '#aaa', marginBottom: 14 }}>Rules for sending a survey to the customer once an appointment is completed</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f3f3f3' }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>Auto-send survey</div>
          <div style={{ fontSize: 11, color: '#aaa' }}>When off, clicking Completed shows a preview before sending</div>
        </div>
        <div onClick={() => setAutoSend(!autoSend)} style={{ width: 42, height: 24, borderRadius: 12, background: autoSend ? '#25d366' : '#ccc', position: 'relative', cursor: 'pointer', flexShrink: 0 }}>
          <div style={{ position: 'absolute', top: 2, left: autoSend ? 20 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left .15s' }} />
        </div>
      </div>
      <div style={{ padding: '12px 0' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 4 }}>Send after</div>
        <select value={delay} onChange={e => setDelay(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' }}>
          <option value="immediate">Immediately</option>
          <option value="1h">1 hour later</option>
          <option value="nextday">Next day</option>
        </select>
        {delay !== 'immediate' && <div style={{ fontSize: 11, color: '#e67e22', marginTop: 6 }}>⚠️ A backend scheduler is required for delayed sending. In preview mode, surveys are sent immediately.</div>}
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 4 }}>Message <span style={{ fontWeight: 400, color: '#aaa' }}>({'{name}'} {'{product}'} {'{link}'} can be used)</span></div>
      <textarea value={message} onChange={e => setMessage(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', minHeight: 100, border: '1px solid #ddd', borderRadius: 8, padding: 10, fontFamily: 'inherit', fontSize: 13 }} />
      <div style={{ fontSize: 11, color: '#aaa', margin: '10px 0 4px' }}>Preview</div>
      <div style={{ background: '#DCF8C6', borderRadius: 10, padding: 12, fontSize: 13, color: '#222', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{preview}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
        <button onClick={save} style={{ padding: '9px 20px', background: '#25d366', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Save</button>
        {toast && <span style={{ fontSize: 12, color: '#2e7d32' }}>{toast}</span>}
      </div>
    </div>
  );
}

// ── Main export ────────────────────────────────────────────────
export function LeadSettings({ agents }) {
  const [activeSection, setActiveSection] = useState('assign');

  const sections = [
    { id: 'assign', label: '⚡ Auto-assign' },
    { id: 'leave', label: '👤 Agent Leave' },
    { id: 'rnr', label: '🔄 RNR' },
    { id: 'reports', label: '📊 Scheduled Reports' },
    { id: 'survey', label: '📋 Feedback Survey' },
  ];

  return (
    <div style={{ padding: 20 }}>
      <div style={{ fontSize: 16, fontWeight: 800, color: '#1a1a2e', marginBottom: 16 }}>⚙️ Lead Settings</div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {sections.map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)} style={{
            padding: '7px 16px', fontSize: 12, fontWeight: 700, border: 'none', borderRadius: 20, cursor: 'pointer', fontFamily: 'inherit',
            background: activeSection === s.id ? '#075e54' : '#f0f0f0',
            color: activeSection === s.id ? '#fff' : '#555',
          }}>{s.label}</button>
        ))}
      </div>

      {activeSection === 'assign' && <AutoAssignRules agents={agents} />}
      {activeSection === 'leave' && <AgentLeaveToggle agents={agents} />}
      {activeSection === 'rnr' && <RNRAutomation />}
      {activeSection === 'reports' && <ScheduledReports />}
      {activeSection === 'survey' && <SurveySettings />}
    </div>
  );
}
